import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';

import {
  FontFamilies,
  FontSizes,
  Theme,
} from 'common';

export const Markdown = ({ markdown, defaultFontSize, padding, gap }) => {
  const renderedContent = useMemo(() => {
    if (!markdown) return '';
    
    // Helper function to process inline markdown elements
    const processInlineMarkdown = (text) => {
      return text
        // Code blocks (before other processing to avoid conflicts)
        //.replace(/`([^`]+)`/g, `<code style="background-color: #f4f4f4; padding: 2px 4px; border-radius: 3px; font-family: monospace; font-size: 0.9em;">$1</code>`)
        // Bold (improved pattern to handle text at beginning of line)
        .replace(/\*\*((?:[^*]|\*(?!\*))+)\*\*/g, `<span style="font-family: ${FontFamilies.Bold} !important;">$1</span>`)
        // Italic
        .replace(/_((?:[^_]|_(?!_))+)_/g, '<em>$1</em>')
        // Strikethrough
        .replace(/~~((?:[^~]|~(?!~))+)~~/g, '<del>$1</del>')
        // Links
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, `<a class="markdown-link" style="font-family: ${FontFamilies.Regular} !important; text-decoration: underline; text-decoration-color: ${Theme.Markdown.TextColor};" href="$2" target="_blank" rel="noopener noreferrer">$1</a>`);
    };

    const lines = markdown.split('\n');
    let html = '';
    let inList = false;
    let listType = null; // 'ul' or 'ol'
    let inCodeBlock = false;
    let codeBlockContent = '';
    let currentParagraph = '';
    let inParagraph = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();
      const nextLine = i < lines.length - 1 ? lines[i + 1].trim() : '';
      
      if (inCodeBlock) {
        codeBlockContent += (codeBlockContent ? '\n' : '') + line;
        continue;
      }
      
      // Check if this is a list item (unordered or ordered)
      const isUnorderedList = trimmedLine.match(/^[*-]\s+(.+)$/);
      const isOrderedList = trimmedLine.match(/^\d+\.\s+(.+)$/);
      
      if (isUnorderedList || isOrderedList) {
        // Close any open paragraph before starting a list
        if (inParagraph) {
          html += `<p style="line-height: 1.5; font-size: ${defaultFontSize ?? FontSizes.Small};">${currentParagraph}</p>`;
          currentParagraph = '';
          inParagraph = false;
        }

        const newListType = isUnorderedList ? 'ul' : 'ol';
        
        if (!inList || listType !== newListType) {
          // Close previous list if exists
          if (inList) {
            html += `</${listType}>`;
          }
          // Start a new list
          html += `<${newListType} class="markdown-${newListType}" style="font-size: ${defaultFontSize ?? FontSizes.Small}; margin: 0; padding-left: 20px;">`;
          inList = true;
          listType = newListType;
        }
        
        // Extract and process list item content
        const content = isUnorderedList ? isUnorderedList[1] : isOrderedList[1];
        const processedContent = processInlineMarkdown(content);
        html += `<li><span style="font-family: ${FontFamilies.Regular} !important;">${processedContent}</span></li>`;
      } else if (inList && trimmedLine === '' && !nextLine.match(/^(?:[*-]|\d+\.)\s+/)) {
        // Close the list if we have an empty line and next line is not a list item
        html += `</${listType}>`;
        inList = false;
        listType = null;
      } else if (!inList) {
        // Process other markdown elements
        let processed = '';
        
        // Headers
        if (trimmedLine.match(/^# /)) {
          // Close any open paragraph before starting a header
          if (inParagraph) {
            html += `<p style="line-height: 1.5; font-size: ${defaultFontSize ?? FontSizes.Small};">${currentParagraph}</p>`;
            currentParagraph = '';
            inParagraph = false;
          }
          
          // Process the header content for inline markdown
          const headerContent = trimmedLine.replace(/^#\s+/, '');
          const processedHeader = processInlineMarkdown(headerContent);
          processed = `<h1 style="font-family: ${FontFamilies.Regular} !important;">${processedHeader}</h1>`;
          html += processed;
        } else if (trimmedLine.match(/^## /)) {
          // Close any open paragraph before starting a header
          if (inParagraph) {
            html += `<p style="line-height: 1.5; font-size: ${defaultFontSize ?? FontSizes.Small};">${currentParagraph}</p>`;
            currentParagraph = '';
            inParagraph = false;
          }
          
          // Process the header content for inline markdown
          const headerContent = trimmedLine.replace(/^##\s+/, '');
          const processedHeader = processInlineMarkdown(headerContent);
          processed = `<h2 style="font-family: ${FontFamilies.Regular} !important;">${processedHeader}</h2>`;
          html += processed;
        } else if (trimmedLine.match(/^### /)) {
          // Close any open paragraph before starting a header
          if (inParagraph) {
            html += `<p style="line-height: 1.5; font-size: ${defaultFontSize ?? FontSizes.Small};">${currentParagraph}</p>`;
            currentParagraph = '';
            inParagraph = false;
          }
          
          // Process the header content for inline markdown
          const headerContent = trimmedLine.replace(/^###\s+/, '');
          const processedHeader = processInlineMarkdown(headerContent);
          processed = `<h3 style="font-family: ${FontFamilies.Regular} !important; font-size: 1.17em !important;">${processedHeader}</h3>`;
          html += processed;
        } else if (trimmedLine === '') {
          // Empty line - close current paragraph if exists
          if (inParagraph) {
            html += `<p style="line-height: 1.5; font-size: ${defaultFontSize ?? FontSizes.Small};">${currentParagraph}</p>`;
            currentParagraph = '';
            inParagraph = false;
          }
        } else {
          // Regular paragraph content
          processed = processInlineMarkdown(trimmedLine);
          
          if (processed.trim()) {
            if (inParagraph) {
              // Add a line break for single line breaks
              currentParagraph += '<br/>' + processed;
            } else {
              // Start a new paragraph
              currentParagraph = processed;
              inParagraph = true;
            }
          }
        }
      }
    }

    // Close any open paragraph at the end
    if (inParagraph) {
      html += `<p style="line-height: 1.5; font-size: ${defaultFontSize ?? FontSizes.Small};">${currentParagraph}</p>`;
    }

    // Close any open list at the end
    if (inList) {
      html += `</${listType}>`;
    }
    
    // Close any open code block at the end (shouldn't happen with well-formed markdown)
    if (inCodeBlock) {
      html += `<pre style="background-color: #f4f4f4; padding: 12px; border-radius: 6px; overflow-x: auto; font-family: monospace; font-size: 0.9em; line-height: 1.4;"><code>${DOMPurify.sanitize(codeBlockContent)}</code></pre>`;
    }

    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['h1', 'h2', 'h3', 'p', 'strong', 'em', 'code', 'pre', 'a', 'span', 'ul', 'ol', 'li', 'br', 'del'],
      ALLOWED_ATTR: ['href', 'class', 'style', 'target', 'rel']
    });
  }, [markdown, defaultFontSize]);

  return (
    <div 
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: gap ?? '20px',
        color: Theme.Markdown.TextColor,
        padding: padding ?? '0',
        fontSize: defaultFontSize ?? FontSizes.Small,
        lineHeight: 1.5,
      }}
      dangerouslySetInnerHTML={{ __html: renderedContent }}
    />
  );
};
